import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFCollectiblesManager = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [collectibles, setCollectibles] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    
    const [newCollectible, setNewCollectible] = useState({
        type: 'art',
        description: '',
        purchaseDate: '',
        purchasePrice: 0,
        currentValue: 0,
        location: '',
        insured: false,
        insuranceValue: 0,
        valuer: '',
        lastValuationDate: ''
    });

    // Collectibles categories
    const collectibleTypes = {
        art: { name: 'Artwork & Paintings', icon: '🎨', maxPercent: 5 },
        wine: { name: 'Wine Collections', icon: '🍷', maxPercent: 5 },
        cars: { name: 'Vintage Cars', icon: '🚗', maxPercent: 5 },
        jewelry: { name: 'Jewelry & Precious Metals', icon: '💎', maxPercent: 5 },
        stamps: { name: 'Stamps & Coins', icon: '📮', maxPercent: 5 },
        antiques: { name: 'Antiques & Furniture', icon: '🪑', maxPercent: 5 },
        other: { name: 'Other Collectibles', icon: '📦', maxPercent: 5 }
    };

    // Compliance rules for collectibles
    const COLLECTIBLES_RULES = {
        maxPercentage: 5, // Maximum 5% of total SMSF assets
        prohibitedPersonalUse: true,
        requiresArmslengthPurchase: true,
        requiresIndependentValuation: true,
        requiresInsurance: true,
        storageMustBeSeparate: true,
        noLeaseToMembers: true
    };

    // Calculate collectibles metrics
    const calculateMetrics = () => {
        const totalCollectiblesValue = collectibles.reduce((sum, item) => sum + item.currentValue, 0);
        const totalSMSFValue = smsfState.smsfData?.balance || 1000000;
        const collectiblesPercent = (totalCollectiblesValue / totalSMSFValue) * 100;
        
        const totalPurchasePrice = collectibles.reduce((sum, item) => sum + item.purchasePrice, 0);
        const totalCapitalGain = totalCollectiblesValue - totalPurchasePrice;
        const averageGain = totalPurchasePrice > 0 ? ((totalCapitalGain / totalPurchasePrice) * 100) : 0;
        
        const insuredCount = collectibles.filter(item => item.insured).length;
        const totalInsuredValue = collectibles.reduce((sum, item) => 
            item.insured ? sum + item.insuranceValue : sum, 0
        );
        
        const needsValuation = collectibles.filter(item => {
            if (!item.lastValuationDate) return true;
            const lastValuation = new Date(item.lastValuationDate);
            const threeYearsAgo = new Date();
            threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
            return lastValuation < threeYearsAgo;
        }).length;

        return {
            totalValue: totalCollectiblesValue,
            totalPercent: collectiblesPercent,
            totalPurchasePrice,
            totalCapitalGain,
            averageGain,
            count: collectibles.length,
            insuredCount,
            totalInsuredValue,
            needsValuation,
            isCompliant: collectiblesPercent <= COLLECTIBLES_RULES.maxPercentage
        };
    };

    const metrics = calculateMetrics();

    // Add new collectible
    const handleAddCollectible = () => {
        if (newCollectible.description && newCollectible.purchasePrice > 0) {
            if (editingId) {
                // Update existing
                setCollectibles(collectibles.map(item => 
                    item.id === editingId ? { ...newCollectible, id: editingId } : item
                ));
                setEditingId(null);
            } else {
                // Add new
                setCollectibles([...collectibles, { ...newCollectible, id: Date.now() }]);
            }
            setNewCollectible({
                type: 'art',
                description: '',
                purchaseDate: '',
                purchasePrice: 0,
                currentValue: 0,
                location: '',
                insured: false,
                insuranceValue: 0,
                valuer: '',
                lastValuationDate: ''
            });
            setShowAddForm(false);
        }
    };
    
    // Edit collectible
    const handleEditCollectible = (item) => {
        setNewCollectible({ ...item });
        setEditingId(item.id);
        setShowAddForm(true);
    };

    // Remove collectible
    const handleRemoveCollectible = (id) => {
        setCollectibles(collectibles.filter(item => item.id !== id));
    };

    // Check compliance
    const checkCompliance = () => {
        const issues = [];
        const warnings = [];

        // Percentage check
        if (metrics.totalPercent > COLLECTIBLES_RULES.maxPercentage) {
            issues.push({
                severity: 'critical',
                message: `Collectibles (${metrics.totalPercent.toFixed(1)}%) exceed ${COLLECTIBLES_RULES.maxPercentage}% limit`,
                remedy: `Reduce collectibles value by $${((metrics.totalValue - (metrics.totalValue * 0.05))).toLocaleString()}`
            });
        }

        // Insurance warnings
        if (metrics.insuredCount < collectibles.length) {
            warnings.push({
                severity: 'warning',
                message: `${collectibles.length - metrics.insuredCount} collectible(s) not insured`,
                remedy: 'All collectibles should be adequately insured'
            });
        }

        // Valuation warnings
        if (metrics.needsValuation > 0) {
            warnings.push({
                severity: 'warning',
                message: `${metrics.needsValuation} collectible(s) need revaluation`,
                remedy: 'Collectibles should be revalued every 3 years minimum'
            });
        }

        // No collectibles warning
        if (collectibles.length === 0) {
            warnings.push({
                severity: 'info',
                message: 'No collectibles currently tracked',
                remedy: 'Add collectibles to monitor compliance and valuations'
            });
        }

        return { issues, warnings };
    };

    const compliance = checkCompliance();

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Compliance Status */}
            <div className={`p-4 rounded-lg border-2 ${
                metrics.isCompliant ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            }`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold">
                            {metrics.isCompliant ? '✅ Collectibles Compliant' : '❌ Compliance Issue'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {metrics.isCompliant 
                                ? `Collectibles at ${metrics.totalPercent.toFixed(1)}% of total assets (under 5% limit)`
                                : `Collectibles at ${metrics.totalPercent.toFixed(1)}% exceed 5% regulatory limit`
                            }
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold">{metrics.totalPercent.toFixed(1)}%</div>
                        <div className="text-sm text-gray-600">of Total Assets</div>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Collectibles Portfolio Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">{metrics.count}</div>
                        <div className="text-sm text-blue-700">Total Collectibles</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">${metrics.totalValue.toLocaleString()}</div>
                        <div className="text-sm text-green-700">Current Value</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-600">{metrics.averageGain.toFixed(1)}%</div>
                        <div className="text-sm text-purple-700">Average Gain</div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-orange-600">${metrics.totalInsuredValue.toLocaleString()}</div>
                        <div className="text-sm text-orange-700">Insured Value</div>
                    </div>
                </div>
            </div>

            {/* Collectibles by Type */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Collectibles by Category</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(collectibleTypes).map(([key, type]) => {
                        const typeItems = collectibles.filter(item => item.type === key);
                        const typeValue = typeItems.reduce((sum, item) => sum + item.currentValue, 0);
                        
                        return (
                            <div key={key} className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <span className="text-2xl mr-2">{type.icon}</span>
                                    <h4 className="font-semibold text-gray-800">{type.name}</h4>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Count:</span>
                                        <span className="font-medium">{typeItems.length}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Value:</span>
                                        <span className="font-medium">${typeValue.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Compliance Issues */}
            {(compliance.issues.length > 0 || compliance.warnings.length > 0) && (
                <div className="space-y-4">
                    {compliance.issues.map((issue, index) => (
                        <div key={index} className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h4 className="font-semibold text-red-800">Critical Issue</h4>
                            <p className="text-sm text-red-700">{issue.message}</p>
                            <p className="text-sm text-red-600 mt-2"><strong>Remedy:</strong> {issue.remedy}</p>
                        </div>
                    ))}
                    {compliance.warnings.map((warning, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            warning.severity === 'warning' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'
                        }`}>
                            <h4 className={`font-semibold ${
                                warning.severity === 'warning' ? 'text-yellow-800' : 'text-blue-800'
                            }`}>
                                {warning.severity === 'warning' ? 'Warning' : 'Information'}
                            </h4>
                            <p className={`text-sm ${
                                warning.severity === 'warning' ? 'text-yellow-700' : 'text-blue-700'
                            }`}>
                                {warning.message}
                            </p>
                            <p className={`text-sm mt-2 ${
                                warning.severity === 'warning' ? 'text-yellow-600' : 'text-blue-600'
                            }`}>
                                <strong>Note:</strong> {warning.remedy}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderInventory = () => (
        <div className="space-y-6">
            {/* Add New Collectible Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    {showAddForm ? 'Cancel' : '+ Add Collectible'}
                </button>
            </div>

            {/* Add Form */}
            {showAddForm && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Collectible' : 'Add New Collectible'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                            <select
                                value={newCollectible.type}
                                onChange={(e) => setNewCollectible({ ...newCollectible, type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                {Object.entries(collectibleTypes).map(([key, type]) => (
                                    <option key={key} value={key}>{type.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <input
                                type="text"
                                value={newCollectible.description}
                                onChange={(e) => setNewCollectible({ ...newCollectible, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="e.g., Picasso 1960 Original"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                            <input
                                type="date"
                                value={newCollectible.purchaseDate}
                                onChange={(e) => setNewCollectible({ ...newCollectible, purchaseDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price ($)</label>
                            <input
                                {...getNumericInputProps()}
                                value={newCollectible.purchasePrice}
                                onChange={(e) => setNewCollectible({ ...newCollectible, purchasePrice: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Value ($)</label>
                            <input
                                {...getNumericInputProps()}
                                value={newCollectible.currentValue}
                                onChange={(e) => setNewCollectible({ ...newCollectible, currentValue: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Storage Location</label>
                            <input
                                type="text"
                                value={newCollectible.location}
                                onChange={(e) => setNewCollectible({ ...newCollectible, location: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="e.g., Secure vault, art gallery"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Valuer</label>
                            <input
                                type="text"
                                value={newCollectible.valuer}
                                onChange={(e) => setNewCollectible({ ...newCollectible, valuer: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Independent valuer name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Last Valuation Date</label>
                            <input
                                type="date"
                                value={newCollectible.lastValuationDate}
                                onChange={(e) => setNewCollectible({ ...newCollectible, lastValuationDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        
                        {/* Type-Specific Fields */}
                        {newCollectible.type === 'art' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Artist Name</label>
                                    <input
                                        type="text"
                                        value={newCollectible.artistName || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, artistName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Pablo Picasso"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Artwork Medium</label>
                                    <select
                                        value={newCollectible.medium || 'oil'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, medium: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="oil">Oil Painting</option>
                                        <option value="acrylic">Acrylic</option>
                                        <option value="watercolor">Watercolor</option>
                                        <option value="sculpture">Sculpture</option>
                                        <option value="print">Print/Lithograph</option>
                                        <option value="mixed">Mixed Media</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Dimensions (cm)</label>
                                    <input
                                        type="text"
                                        value={newCollectible.dimensions || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, dimensions: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., 100 x 80"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Provenance</label>
                                    <input
                                        type="text"
                                        value={newCollectible.provenance || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, provenance: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Ownership history"
                                    />
                                </div>
                            </>
                        )}
                        
                        {newCollectible.type === 'wine' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Wine Region</label>
                                    <input
                                        type="text"
                                        value={newCollectible.region || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, region: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Barossa Valley"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Vintage Year</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={newCollectible.vintage || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, vintage: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., 2010"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Bottles</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={newCollectible.bottles || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, bottles: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., 12"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Storage Conditions</label>
                                    <select
                                        value={newCollectible.storageConditions || 'climate'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, storageConditions: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="climate">Climate-Controlled Cellar</option>
                                        <option value="professional">Professional Wine Storage</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </>
                        )}
                        
                        {newCollectible.type === 'cars' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Make & Model</label>
                                    <input
                                        type="text"
                                        value={newCollectible.makeModel || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, makeModel: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Ferrari 250 GTO"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={newCollectible.year || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, year: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., 1962"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">VIN/Chassis Number</label>
                                    <input
                                        type="text"
                                        value={newCollectible.vin || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, vin: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Vehicle identification"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                                    <select
                                        value={newCollectible.condition || 'excellent'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, condition: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="concours">Concours (Show Quality)</option>
                                        <option value="excellent">Excellent</option>
                                        <option value="good">Good</option>
                                        <option value="fair">Fair</option>
                                        <option value="restoration">Needs Restoration</option>
                                    </select>
                                </div>
                            </>
                        )}
                        
                        {newCollectible.type === 'jewelry' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Jewelry Type</label>
                                    <select
                                        value={newCollectible.jewelryType || 'ring'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, jewelryType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="ring">Ring</option>
                                        <option value="necklace">Necklace</option>
                                        <option value="bracelet">Bracelet</option>
                                        <option value="earrings">Earrings</option>
                                        <option value="watch">Watch</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Metal Type</label>
                                    <input
                                        type="text"
                                        value={newCollectible.metalType || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, metalType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., 18K Gold, Platinum"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Gemstones</label>
                                    <input
                                        type="text"
                                        value={newCollectible.gemstones || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, gemstones: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., 2ct Diamond, Sapphire"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Number</label>
                                    <input
                                        type="text"
                                        value={newCollectible.certificateNumber || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, certificateNumber: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Gemological certificate"
                                    />
                                </div>
                            </>
                        )}
                        
                        {newCollectible.type === 'stamps' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Collection Type</label>
                                    <select
                                        value={newCollectible.collectionType || 'stamps'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, collectionType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="stamps">Stamps</option>
                                        <option value="coins">Coins</option>
                                        <option value="banknotes">Banknotes</option>
                                        <option value="mixed">Mixed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Country/Region</label>
                                    <input
                                        type="text"
                                        value={newCollectible.countryRegion || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, countryRegion: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Australian, British Empire"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Era/Period</label>
                                    <input
                                        type="text"
                                        value={newCollectible.eraPeriod || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, eraPeriod: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Victorian Era, 1920s"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Catalogue Reference</label>
                                    <input
                                        type="text"
                                        value={newCollectible.catalogueRef || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, catalogueRef: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Stanley Gibbons SG123"
                                    />
                                </div>
                            </>
                        )}
                        
                        {newCollectible.type === 'antiques' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Antique Category</label>
                                    <select
                                        value={newCollectible.antiqueCategory || 'furniture'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, antiqueCategory: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="furniture">Furniture</option>
                                        <option value="ceramics">Ceramics/Pottery</option>
                                        <option value="glassware">Glassware</option>
                                        <option value="silverware">Silverware</option>
                                        <option value="clocks">Clocks/Watches</option>
                                        <option value="books">Rare Books</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Period/Style</label>
                                    <input
                                        type="text"
                                        value={newCollectible.periodStyle || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, periodStyle: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Georgian, Art Deco"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Maker/Manufacturer</label>
                                    <input
                                        type="text"
                                        value={newCollectible.maker || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, maker: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Thomas Chippendale"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Condition/Restoration</label>
                                    <select
                                        value={newCollectible.conditionRestoration || 'original'}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, conditionRestoration: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="original">Original/Unrestored</option>
                                        <option value="professional">Professionally Restored</option>
                                        <option value="partial">Partially Restored</option>
                                        <option value="reproduction">Reproduction</option>
                                    </select>
                                </div>
                            </>
                        )}
                        
                        {newCollectible.type === 'other' && (
                            <>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Description</label>
                                    <textarea
                                        value={newCollectible.detailedDescription || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, detailedDescription: e.target.value })}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Provide detailed description of the collectible"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                    <input
                                        type="text"
                                        value={newCollectible.category || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, category: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g., Memorabilia, Musical Instruments"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Unique Features</label>
                                    <input
                                        type="text"
                                        value={newCollectible.uniqueFeatures || ''}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, uniqueFeatures: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Notable characteristics or rarity"
                                    />
                                </div>
                            </>
                        )}
                        
                        {/* Storage with Related Party Warning */}
                        <div className="md:col-span-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={newCollectible.storedWithRelatedParty || false}
                                    onChange={(e) => setNewCollectible({ ...newCollectible, storedWithRelatedParty: e.target.checked })}
                                    className="mr-2 h-4 w-4 text-red-600"
                                />
                                <span className="text-sm font-medium text-gray-700">Storage with Related Party</span>
                            </label>
                            {newCollectible.storedWithRelatedParty && (
                                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                    <p className="text-sm text-yellow-800">
                                        <strong>⚠️ Warning:</strong> Storing collectibles with related parties may breach SMSF rules. 
                                        Ensure proper arm's length arrangements and documentation.
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        {/* Insurance Policy Upload */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Policy Document</label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setNewCollectible({ ...newCollectible, insurancePolicyDoc: e.target.files[0].name });
                                        alert(`Insurance policy "${e.target.files[0].name}" uploaded successfully`);
                                    }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {newCollectible.insurancePolicyDoc && (
                                <p className="text-xs text-green-600 mt-1">✓ {newCollectible.insurancePolicyDoc}</p>
                            )}
                        </div>
                        
                        {/* Insurance Coverage Amount */}
                        <div className="md:col-span-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={newCollectible.insured}
                                    onChange={(e) => setNewCollectible({ ...newCollectible, insured: e.target.checked })}
                                    className="mr-2 h-4 w-4 text-blue-600"
                                />
                                <span className="text-sm font-medium text-gray-700">Insured</span>
                            </label>
                            {newCollectible.insured && (
                                <div className="mt-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Coverage Amount ($)</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={newCollectible.insuranceValue}
                                        onChange={(e) => setNewCollectible({ ...newCollectible, insuranceValue: Number(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Must be ≥ current value"
                                    />
                                    {newCollectible.insuranceValue < newCollectible.currentValue && newCollectible.insuranceValue > 0 && (
                                        <p className="text-xs text-red-600 mt-1">
                                            ⚠️ Insurance coverage must be at least equal to current value (${newCollectible.currentValue.toLocaleString()})
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        {/* Lease Agreement Upload (with error) */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Lease Agreement (if rented to trustee/member)</label>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        alert('❌ ERROR: Collectibles cannot be leased or rented to members, trustees, or related parties. This is prohibited under SMSF rules.');
                                        e.target.value = '';
                                        setNewCollectible({ ...newCollectible, leaseAgreementDoc: null });
                                    }
                                }}
                                className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-red-50"
                            />
                            <p className="text-xs text-red-600 mt-1">
                                ⚠️ Uploading a lease agreement will trigger an error - leasing collectibles to trustees/members is prohibited
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                        <button
                            onClick={() => {
                                setShowAddForm(false);
                                setEditingId(null);
                                setNewCollectible({
                                    type: 'art',
                                    description: '',
                                    purchaseDate: '',
                                    purchasePrice: 0,
                                    currentValue: 0,
                                    location: '',
                                    insured: false,
                                    insuranceValue: 0,
                                    valuer: '',
                                    lastValuationDate: ''
                                });
                            }}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddCollectible}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            {editingId ? 'Update Collectible' : 'Add Collectible'}
                        </button>
                    </div>
                </div>
            )}

            {/* Collectibles List */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Collectibles Inventory</h3>
                {collectibles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No collectibles added yet</p>
                        <p className="text-sm mt-2">Click "Add Collectible" to start tracking</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {collectibles.map((item) => (
                            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center">
                                        <span className="text-2xl mr-3">{collectibleTypes[item.type].icon}</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800">{item.description}</h4>
                                            <p className="text-sm text-gray-600">{collectibleTypes[item.type].name}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEditCollectible(item)}
                                            className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md text-sm border border-blue-300"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleRemoveCollectible(item.id)}
                                            className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-md text-sm border border-red-300"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <div className="text-xs text-gray-600">Purchase Price</div>
                                        <div className="font-medium">${item.purchasePrice.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Current Value</div>
                                        <div className="font-medium text-green-600">${item.currentValue.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Capital Gain</div>
                                        <div className={`font-medium ${
                                            item.currentValue >= item.purchasePrice ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            ${(item.currentValue - item.purchasePrice).toLocaleString()}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Insurance</div>
                                        <div className="font-medium">
                                            {item.insured ? (
                                                <span className="text-green-600">✓ ${item.insuranceValue.toLocaleString()}</span>
                                            ) : (
                                                <span className="text-red-600">✗ Not Insured</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2 text-xs text-gray-600">
                                    <strong>Location:</strong> {item.location || 'Not specified'} | 
                                    <strong> Valuer:</strong> {item.valuer || 'Not specified'} | 
                                    <strong> Last Valuation:</strong> {item.lastValuationDate || 'Never'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderCompliance = () => (
        <div className="space-y-6">
            {/* Compliance Rules */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Collectibles Compliance Rules</h3>
                <div className="space-y-4">
                    {[
                        {
                            rule: 'Asset Limit',
                            description: 'Collectibles cannot exceed 5% of total SMSF assets',
                            status: metrics.totalPercent <= 5,
                            current: `${metrics.totalPercent.toFixed(1)}%`
                        },
                        {
                            rule: 'Personal Use Prohibited',
                            description: 'Members cannot use or enjoy collectibles personally',
                            status: true,
                            current: 'Confirmed'
                        },
                        {
                            rule: 'Arms-Length Purchase',
                            description: 'Must be purchased at market value from unrelated parties',
                            status: true,
                            current: 'Confirmed'
                        },
                        {
                            rule: 'Independent Valuation',
                            description: 'Regular independent valuations required',
                            status: metrics.needsValuation === 0,
                            current: `${metrics.needsValuation} need revaluation`
                        },
                        {
                            rule: 'Adequate Insurance',
                            description: 'Collectibles must be adequately insured',
                            status: metrics.insuredCount === collectibles.length,
                            current: `${metrics.insuredCount}/${collectibles.length} insured`
                        },
                        {
                            rule: 'Separate Storage',
                            description: 'Must be stored separately from personal assets',
                            status: true,
                            current: 'Confirmed'
                        },
                        {
                            rule: 'No Lease to Members',
                            description: 'Cannot be leased to members or related parties',
                            status: true,
                            current: 'Confirmed'
                        }
                    ].map((item, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            item.status ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                        }`}>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mr-3 mt-1">
                                    {item.status ? (
                                        <span className="text-green-600 text-xl">✓</span>
                                    ) : (
                                        <span className="text-red-600 text-xl">✗</span>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h4 className={`font-semibold ${item.status ? 'text-green-800' : 'text-red-800'}`}>
                                        {item.rule}
                                    </h4>
                                    <p className={`text-sm ${item.status ? 'text-green-700' : 'text-red-700'}`}>
                                        {item.description}
                                    </p>
                                    <p className={`text-sm mt-1 ${item.status ? 'text-green-600' : 'text-red-600'}`}>
                                        Current Status: {item.current}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Compliance Tips */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Best Practices</h3>
                <div className="space-y-3">
                    {[
                        'Obtain independent valuations every 3 years minimum',
                        'Keep detailed purchase records and receipts',
                        'Ensure storage is separate from personal property',
                        'Maintain comprehensive insurance coverage',
                        'Document that no personal use occurs',
                        'Review compliance annually',
                        'Consider professional storage facilities',
                        'Keep photographic evidence of items'
                    ].map((tip, index) => (
                        <div key={index} className="flex items-start p-3 border border-gray-200 rounded-lg">
                            <span className="text-blue-600 mr-2">•</span>
                            <p className="text-sm text-gray-700">{tip}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Collectibles Manager</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('inventory')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'inventory' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Inventory
                    </button>
                    <button
                        onClick={() => setActiveTab('compliance')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'compliance' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Compliance
                    </button>
                </div>
            </div>

            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'inventory' && renderInventory()}
            {activeTab === 'compliance' && renderCompliance()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Last Updated: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Inventory
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Schedule Valuation
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFCollectiblesManager.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFCollectiblesManager;
